import React, { useState } from 'react';
import { AlertCircleIcon, CheckCircleIcon, ChevronDownIcon, ClockIcon, DollarSignIcon, FilterIcon, SearchIcon, TrendingUpIcon } from 'lucide-react';
export function InvoiceList({
  vendor,
  onViewInvoice
}) {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  // Sample data for invoices
  const allInvoices = [{
    id: 'INV-2023-001',
    date: '2023-10-15',
    amount: 1250.75,
    status: 'Pending Approval',
    poNumber: 'PO-2023-001',
    description: 'Office Supplies'
  }, {
    id: 'INV-2023-002',
    date: '2023-10-10',
    amount: 3430.0,
    status: 'Approved',
    poNumber: 'PO-2023-002',
    description: 'IT Equipment',
    paymentStatus: 'Partially Paid',
    amountPaid: 1500.0,
    remainingBalance: 1930.0
  }, {
    id: 'INV-2023-003',
    date: '2023-10-05',
    amount: 2100.5,
    status: 'Paid',
    paymentStatus: 'Fully Paid',
    amountPaid: 2100.5,
    remainingBalance: 0,
    poNumber: 'PO-2023-003',
    description: 'Consulting Services'
  }, {
    id: 'INV-2023-004',
    date: '2023-09-28',
    amount: 5600.25,
    status: 'Rejected',
    poNumber: 'PO-2023-004',
    description: 'Marketing Materials'
  }, {
    id: 'INV-2023-005',
    date: '2023-09-20',
    amount: 1800.0,
    status: 'Paid',
    paymentStatus: 'Fully Paid',
    amountPaid: 1800.0,
    remainingBalance: 0,
    poNumber: 'PO-2023-005',
    description: 'Software Licenses'
  }, {
    id: 'INV-2023-006',
    date: '2023-09-15',
    amount: 950.75,
    status: 'Pending Approval',
    poNumber: 'PO-2023-006',
    description: 'Office Furniture'
  }, {
    id: 'INV-2023-007',
    date: '2023-09-10',
    amount: 3200.0,
    status: 'Approved',
    paymentStatus: 'Partially Paid',
    amountPaid: 1000.0,
    remainingBalance: 2200.0,
    poNumber: 'PO-2023-007',
    description: 'Training Services'
  }, {
    id: 'INV-2023-008',
    date: '2023-09-05',
    amount: 1450.25,
    status: 'Pending Approval',
    poNumber: 'PO-2023-008',
    description: 'Hardware Supplies'
  }];
  // Filter and sort invoices
  const filteredInvoices = allInvoices.filter(invoice => {
    if (filterStatus !== 'all' && invoice.paymentStatus !== filterStatus && invoice.status !== filterStatus) {
      return false;
    }
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return invoice.id.toLowerCase().includes(search) || invoice.poNumber.toLowerCase().includes(search) || invoice.description.toLowerCase().includes(search);
    }
    return true;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'date') {
      comparison = new Date(a.date) - new Date(b.date);
    } else if (sortBy === 'amount') {
      comparison = a.amount - b.amount;
    } else if (sortBy === 'id') {
      comparison = a.id.localeCompare(b.id);
    }
    return sortDirection === 'desc' ? -comparison : comparison;
  });
  const getStatusIcon = status => {
    switch (status) {
      case 'Pending Approval':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-617" />;
      case 'Approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-618" />;
      case 'Paid':
      case 'Fully Paid':
        return <TrendingUpIcon className="h-5 w-5 text-blue-500" data-id="element-619" />;
      case 'Partially Paid':
        return <DollarSignIcon className="h-5 w-5 text-blue-400" data-id="element-620" />;
      case 'Rejected':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-621" />;
      default:
        return null;
    }
  };
  const toggleSort = field => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  return <div data-id="element-622">
      <div className="mb-8" data-id="element-623">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-624">Invoices</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-625">
          View and manage all your submitted invoices
        </p>
      </div>
      {/* Filters and search */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0" data-id="element-626">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-627">
          <div className="relative" data-id="element-628">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-629">
              <FilterIcon className="h-4 w-4 text-gray-400" data-id="element-630" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="pl-9 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-631">
              <option value="all" data-id="element-632">All Statuses</option>
              <option value="Pending Approval" data-id="element-633">Pending Approval</option>
              <option value="Approved" data-id="element-634">Approved</option>
              <option value="Fully Paid" data-id="element-635">Fully Paid</option>
              <option value="Partially Paid" data-id="element-636">Partially Paid</option>
              <option value="Rejected" data-id="element-637">Rejected</option>
            </select>
          </div>
          <div className="relative rounded-md shadow-sm" data-id="element-638">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-639">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-640" />
            </div>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search invoices..." className="pl-9 pr-3 py-2 block w-full sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" data-id="element-641" />
          </div>
        </div>
        <button onClick={() => {}} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-642">
          Upload New Invoice
        </button>
      </div>
      {/* Invoices table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-643">
        <div className="overflow-x-auto" data-id="element-644">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-645">
            <thead className="bg-gray-50" data-id="element-646">
              <tr data-id="element-647">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('id')} data-id="element-648">
                  <div className="flex items-center space-x-1" data-id="element-649">
                    <span data-id="element-650">Invoice #</span>
                    {sortBy === 'id' && <ChevronDownIcon className={`h-4 w-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} data-id="element-651" />}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('date')} data-id="element-652">
                  <div className="flex items-center space-x-1" data-id="element-653">
                    <span data-id="element-654">Date</span>
                    {sortBy === 'date' && <ChevronDownIcon className={`h-4 w-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} data-id="element-655" />}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-656">
                  PO #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-657">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('amount')} data-id="element-658">
                  <div className="flex items-center space-x-1" data-id="element-659">
                    <span data-id="element-660">Amount</span>
                    {sortBy === 'amount' && <ChevronDownIcon className={`h-4 w-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} data-id="element-661" />}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-662">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-663">
                  <span className="sr-only" data-id="element-664">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-665">
              {filteredInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-666">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-667">
                    {invoice.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-668">
                    {invoice.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-669">
                    {invoice.poNumber}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" data-id="element-670">
                    {invoice.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-671">
                    ${invoice.amount.toFixed(2)}
                    {invoice.paymentStatus === 'Partially Paid' && <div className="text-xs text-indigo-600" data-id="element-672">
                        ${invoice.amountPaid.toFixed(2)} paid
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-673">
                    <div className="flex items-center" data-id="element-674">
                      {getStatusIcon(invoice.paymentStatus || invoice.status)}
                      <span className={`ml-1.5 text-xs font-medium ${invoice.paymentStatus === 'Fully Paid' ? 'text-blue-800' : invoice.paymentStatus === 'Partially Paid' ? 'text-indigo-800' : invoice.status === 'Approved' ? 'text-green-800' : invoice.status === 'Pending Approval' ? 'text-yellow-800' : 'text-red-800'}`} data-id="element-675">
                        {invoice.paymentStatus || invoice.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-676">
                    <button onClick={() => onViewInvoice(invoice)} className="text-indigo-600 hover:text-indigo-900" data-id="element-677">
                      View
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredInvoices.length === 0 && <div className="px-6 py-10 text-center" data-id="element-678">
            <p className="text-gray-500 text-sm" data-id="element-679">
              No invoices found matching your criteria.
            </p>
          </div>}
      </div>
    </div>;
}